/**
 * Parsing e formatação do WKT (Well-Known Text) usado pelo Traccar para
 * armazenar áreas de geocerca. Formatos aceitos:
 *
 *   CIRCLE (lat lon, radius_meters)
 *   POLYGON ((lat1 lon1, lat2 lon2, ..., lat1 lon1))
 *   LINESTRING (lat1 lon1, lat2 lon2, ..., latN lonN)
 *
 * IMPORTANTE: a ordem dos pares é "lat lon" (espaço), não "lon lat" como em
 * GeoJSON. Polígonos precisam ser fechados (primeiro ponto = último).
 */

export type GeofenceShape = 'CIRCLE' | 'POLYGON' | 'LINESTRING';

export interface CircleArea {
  type: 'CIRCLE';
  /** [lng, lat] em ordem GeoJSON (no WKT a ordem é lat lon) */
  center: [number, number];
  radiusMeters: number;
}

export interface PolygonArea {
  type: 'POLYGON';
  /** lista [lng, lat] em ordem GeoJSON, sem fechar (helper fecha) */
  ring: [number, number][];
}

export interface LineArea {
  type: 'LINESTRING';
  line: [number, number][];
}

export type ParsedArea = CircleArea | PolygonArea | LineArea;

const NUMBER = /-?\d+(?:\.\d+)?/.source;
const PAIR = new RegExp(`(${NUMBER})\\s+(${NUMBER})`);

/** Divide a string em pares "lat lon" e devolve [lng, lat]. */
function parsePairs(inner: string): [number, number][] {
  return inner
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const m = p.match(PAIR);
      if (!m) throw new Error(`Par lat/lon inválido em WKT: "${p}"`);
      const lat = Number(m[1]);
      const lng = Number(m[2]);
      return [lng, lat] as [number, number];
    });
}

export function parseWkt(wkt: string | null | undefined): ParsedArea | null {
  if (!wkt) return null;
  const s = wkt.trim();

  const circleMatch = s.match(
    new RegExp(`^CIRCLE\\s*\\(\\s*(${NUMBER})\\s+(${NUMBER})\\s*,\\s*(${NUMBER})\\s*\\)$`, 'i'),
  );
  if (circleMatch) {
    const lat = Number(circleMatch[1]);
    const lng = Number(circleMatch[2]);
    const radius = Number(circleMatch[3]);
    return { type: 'CIRCLE', center: [lng, lat], radiusMeters: radius };
  }

  const polyMatch = s.match(/^POLYGON\s*\(\s*\((.+)\)\s*\)\s*$/i);
  if (polyMatch) {
    const ring = parsePairs(polyMatch[1]);
    return { type: 'POLYGON', ring };
  }

  const lineMatch = s.match(/^LINESTRING\s*\((.+)\)\s*$/i);
  if (lineMatch) {
    return { type: 'LINESTRING', line: parsePairs(lineMatch[1]) };
  }

  return null;
}

/** Formata como WKT do Traccar (lat lon, ordem invertida em relação a GeoJSON). */
/** Distância haversine em metros entre dois pontos [lng,lat]. */
function haversineMeters(a: [number, number], b: [number, number]): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** O ponto [lng,lat] está dentro da área? CIRCLE: raio · POLYGON: ray casting · LINESTRING: false. */
export function pointInArea(lng: number, lat: number, area: ParsedArea): boolean {
  if (area.type === 'CIRCLE') {
    return haversineMeters([lng, lat], area.center) <= area.radiusMeters;
  }
  if (area.type === 'POLYGON') {
    const r = area.ring;
    let inside = false;
    for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
      const xi = r[i][0], yi = r[i][1], xj = r[j][0], yj = r[j][1];
      const intersect = (yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }
  return false;
}

export function formatWkt(area: ParsedArea): string {
  const pair = ([lng, lat]: [number, number]) => `${lat} ${lng}`;
  switch (area.type) {
    case 'CIRCLE': {
      const [lng, lat] = area.center;
      return `CIRCLE (${lat} ${lng}, ${Math.round(area.radiusMeters)})`;
    }
    case 'POLYGON': {
      const ring = [...area.ring];
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (!first || !last) throw new Error('Polígono precisa de ao menos 3 pontos');
      if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first);
      return `POLYGON ((${ring.map(pair).join(', ')}))`;
    }
    case 'LINESTRING':
      if (area.line.length < 2) throw new Error('Linha precisa de ao menos 2 pontos');
      return `LINESTRING (${area.line.map(pair).join(', ')})`;
  }
}

/** Converte uma área em GeoJSON Feature pra renderização no mapa. */
export function areaToGeoJSON(
  area: ParsedArea,
): GeoJSON.Feature<GeoJSON.Geometry> {
  if (area.type === 'CIRCLE') {
    return circleAsPolygonFeature(area.center[0], area.center[1], area.radiusMeters);
  }
  if (area.type === 'POLYGON') {
    const ring = [...area.ring];
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first && last && (first[0] !== last[0] || first[1] !== last[1])) ring.push(first);
    return {
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: [ring] },
    };
  }
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates: area.line },
  };
}

function circleAsPolygonFeature(
  lng: number,
  lat: number,
  radiusMeters: number,
  points = 64,
): GeoJSON.Feature<GeoJSON.Polygon> {
  const earthRadius = 6378137;
  const latRad = (lat * Math.PI) / 180;
  const dLatBase = (180 / Math.PI) / earthRadius;
  const dLngBase = dLatBase / Math.cos(latRad);
  const coords: [number, number][] = [];
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dx = radiusMeters * Math.cos(angle);
    const dy = radiusMeters * Math.sin(angle);
    coords.push([lng + dx * dLngBase, lat + dy * dLatBase]);
  }
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [coords] },
  };
}
